import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import PixPayment from "./PixPayment";
import { brl } from "./PurchaseDialog";

const Row = ({ l, v, cls = "", testid }) => (
  <div className="flex justify-between"><span>{l}</span><span className={`font-mono ${cls}`} data-testid={testid}>{v}</span></div>
);

export const PartnerPurchaseDialog = ({ company, balance, onClose, onDone }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [method, setMethod] = useState("balance");
  const [busy, setBusy] = useState(false);
  const [pix, setPix] = useState(null);
  if (!company) return null;
  const amount = Number(value.replace(",", ".")) || 0;
  const cashback = Math.round(amount * company.buyer_percent) / 100;
  const insufficient = method === "balance" && amount > balance;
  const close = () => { setPix(null); setValue(""); onClose(); };

  const submit = async () => {
    if (!window.confirm(`Confirmar compra de ${brl(amount)} com ${company.name}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/partner-purchases", { company_id: company.id, amount, method });
      if (method === "pix") { setPix(data.pix); return; }
      toast.success("Compra registrada! Valide quando receber o produto/serviço.");
      onDone(); close();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const paid = () => { toast.success("Pix confirmado! Valide a compra quando receber."); onDone(); close(); navigate("/parceiros?aba=compras"); };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="partner-purchase-dialog">
        <DialogHeader>
          <DialogTitle>{pix ? "Pague o Pix" : `Comprar com ${company.name}`}</DialogTitle>
          <DialogDescription>O pagamento fica em análise até você validar o recebimento. Depois disso você recebe {company.buyer_percent}% de volta no saldo.</DialogDescription>
        </DialogHeader>
        {pix ? <PixPayment initial={pix} onPaid={paid} /> : (
          <div className="space-y-4 text-sm">
            <div className="space-y-1.5"><Label htmlFor="pp-amount">Valor da compra (R$)</Label>
              <Input id="pp-amount" inputMode="decimal" placeholder="Ex.: 100,00" value={value} onChange={(e) => setValue(e.target.value)} data-testid="partner-purchase-amount" /></div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {[["balance", `Saldo (${brl(balance)})`], ["pix", "Gerar Pix"]].map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={method === v} onClick={() => setMethod(v)}
                        className={`p-3 rounded-lg border text-sm ${method === v ? "gold-border bg-amber-500/5 font-medium" : "border-border text-muted-foreground"}`}
                        data-testid={`partner-purchase-method-${v}`}>{l}</button>
              ))}
            </div>
            <div className="rounded-xl bg-muted/50 p-4 space-y-2">
              <Row l="Você paga" v={brl(amount)} />
              <Row l={`Você recebe de volta (${company.buyer_percent}%)`} v={`+ ${brl(cashback)}`} cls="text-emerald-500" testid="partner-purchase-cashback" />
            </div>
            {insufficient && <p className="text-xs text-rose-500" data-testid="partner-purchase-insufficient">Saldo insuficiente — use o Pix.</p>}
            <Button className="gold-btn w-full" disabled={busy || amount < 1 || insufficient} onClick={submit} data-testid="partner-purchase-confirm">
              {busy ? "Processando..." : method === "pix" ? "Confirmar e gerar Pix" : "Confirmar pagamento"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
